import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { StandardSection } from "components/templates/StandardSection";
import { FC, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import { Vimeo } from "@services";
import { MobileOnly, NotMobile } from "components/templates/Breakpoints";
import Image from "next/image";
import { color, fonts } from "@styles";

interface PurpleVideoSectionProps {
  heading: string;
  body: string[];
  imageSrc: string;
  vimeoId: string;
  buttonText?: string;
}

export const PurpleVideoSection: FC<PurpleVideoSectionProps> = ({
  heading,
  body,
  imageSrc,
  vimeoId,
  buttonText,
}) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  return (
    <>
      <VimeoWrapper>
        <Vimeo
          vimeoId={vimeoId}
          isOpen={isVideoOpen}
          onClose={() => {
            setIsVideoOpen(false);
          }}
        />
      </VimeoWrapper>
      <PurpleSection>
        <BackgroundBubble>
          <svg xmlns="http://www.w3.org/2000/svg" width="383" height="417" fill="none">
            <path
              d="M78-120C-31 37-38 221 134 335c173 115 461 116 570-41s-8-464-180-578c-173-114-337 6-446 164Z"
              fill="#EAE1FF"
            />
          </svg>
        </BackgroundBubble>
        <StandardSection>
          <VideoGrid>
            <div>
              <Heading>{heading}</Heading>
              {body.map((p) => (
                <P1 key={p}>{p}</P1>
              ))}
            </div>
            <ThemeContext.Consumer>
              {(props) => {
                return (
                  <ImageContainer
                    onMouseEnter={() => {
                      props?.preloadVideo(vimeoId);
                    }}
                    onClick={() => {
                      props?.triggerVideo();
                    }}
                  >
                    <VideoImage src={imageSrc} alt="" width={576} height={576} />
                    <PlayIcon>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="120"
                        height="120"
                        viewBox="0 0 120 120"
                        fill="none"
                        opacity="0.8"
                      >
                        <mask id="path-1-inside-1_402_202463" fill="white">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M60 120C93.1371 120 120 93.1371 120 60C120 26.8629 93.1371 0 60 0C26.8629 0 0 26.8629 0 60C0 93.1371 26.8629 120 60 120ZM44.2982 34.0921C42.9657 33.2721 41.25 34.2308 41.25 35.7954V88.6372C41.25 90.2018 42.9657 91.1605 44.2982 90.3405L87.2321 63.9196C88.5012 63.1387 88.5012 61.294 87.2321 60.513L44.2982 34.0921Z"
                          />
                        </mask>
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M60 120C93.1371 120 120 93.1371 120 60C120 26.8629 93.1371 0 60 0C26.8629 0 0 26.8629 0 60C0 93.1371 26.8629 120 60 120ZM44.2982 34.0921C42.9657 33.2721 41.25 34.2308 41.25 35.7954V88.6372C41.25 90.2018 42.9657 91.1605 44.2982 90.3405L87.2321 63.9196C88.5012 63.1387 88.5012 61.294 87.2321 60.513L44.2982 34.0921Z"
                          fill="white"
                        />
                        <path
                          d="M44.2982 34.0921L43.7741 34.9438L44.2982 34.0921ZM44.2982 90.3405L44.8223 91.1922L44.2982 90.3405ZM87.2321 63.9196L87.7562 64.7713L87.2321 63.9196ZM87.2321 60.513L87.7562 59.6613H87.7562L87.2321 60.513ZM119 60C119 92.5848 92.5848 119 60 119V121C93.6894 121 121 93.6894 121 60H119ZM60 1C92.5848 1 119 27.4152 119 60H121C121 26.3106 93.6894 -1 60 -1V1ZM1 60C1 27.4152 27.4152 1 60 1V-1C26.3106 -1 -1 26.3106 -1 60H1ZM60 119C27.4152 119 1 92.5848 1 60H-1C-1 93.6894 26.3106 121 60 121V119ZM42.25 35.7954C42.25 35.0131 43.1078 34.5338 43.7741 34.9438L44.8223 33.2405C42.8235 32.0104 40.25 33.4485 40.25 35.7954H42.25ZM42.25 88.6372V35.7954H40.25V88.6372H42.25ZM43.7741 89.4888C43.1078 89.8989 42.25 89.4195 42.25 88.6372H40.25C40.25 90.9841 42.8235 92.4222 44.8223 91.1922L43.7741 89.4888ZM86.708 63.068L43.7741 89.4888L44.8223 91.1922L87.7562 64.7713L86.708 63.068ZM86.708 61.3646C87.3426 61.7551 87.3426 62.6775 86.708 63.068L87.7562 64.7713C89.6598 63.5998 89.6598 60.8328 87.7562 59.6613L86.708 61.3646ZM43.7741 34.9438L86.708 61.3646L87.7562 59.6613L44.8223 33.2405L43.7741 34.9438Z"
                          fill="white"
                          mask="url(#path-1-inside-1_402_202463)"
                        />
                      </svg>
                    </PlayIcon>
                  </ImageContainer>
                );
              }}
            </ThemeContext.Consumer>
          </VideoGrid>
          {buttonText && (
            <MobileOnly>
              <ScrollToTopButton
                onClick={() => {
                  if (typeof document !== "undefined") {
                    document.getElementById("get-your-free-quote-form").scrollIntoView();
                  }
                }}
              >
                {buttonText}
              </ScrollToTopButton>
            </MobileOnly>
          )}
        </StandardSection>
      </PurpleSection>
    </>
  );
};

export const VideoGrid = styled.div<{ gap?: string }>`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  gap: ${(p) => p.gap || "8rem"};

  @media only screen and (max-width: 55rem) {
    grid-template-columns: 1fr;
    row-gap: 1.5rem;
    padding-top: 2rem;
  }
`;

export const Heading = styled(H2)`
  @media only screen and (max-width: 75rem) {
    font-size: 2.5rem;
  }
`;

export const VideoImage = styled(Image)`
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
`;

const ScrollToTopButton = styled.button`
  background: ${color.P100};
  border-radius: 8px;
  font-family: ${fonts.body};
  font-style: normal;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 2rem;
  text-align: center;
  letter-spacing: 0.075rem;
  color: ${color.white};
  border: none;
  padding: 1rem;
  margin-top: 2rem;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0.01, 0.165, 0.99);

  &:hover {
    background-color: #e30d76bb;
  }

  &:focus {
    background-color: #e30d76bb;
  }
`;

const PurpleSection = styled.div`
  position: relative;
  background: ${color.S310};
`;

const BackgroundBubble = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;

  img {
    width: 100%;
  }

  ${NotMobile} {
    svg {
      transition: 300ms ease-in-out;
    }

    &:hover {
      svg {
        transform: translateY(-4rem) scale(1.2);
      }
    }
  }
`;

export const PlayIcon = styled.div`
  position: absolute;

  svg {
    transition: 320ms;
  }

  &:hover {
    svg {
      opacity: 1;
    }
  }
`;

const VimeoWrapper = styled.div`
  position: absolute;
  top: 10.625rem;
  left: 3.4375rem;
`;
